import { Icon, Tooltip as ReactToolTip } from '@analog/ui/src';
import allImgPaths from 'assets/allImgPaths';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { AnalogIcon } from 'components';
import { Skeleton } from 'components/Skeleton';
import { Doughnut } from 'react-chartjs-2';

import { TokenStatsType } from '../../useTelemetry';

import { useTokenChart } from './useTokenChart';

ChartJS.register(ArcElement, Tooltip, Legend);

export const DonutChart = (stats: TokenStatsType) => {
  const {
    data,
    options,
    circulatingSupply,
    staking,
    totalIssuance,
    vesting,
    circulatingSupplyPercentage,
    stakingPercentage,
    vestingPercentage,
  } = useTokenChart(stats);

  return (
    <div className="flex flex-col gap-4 2xl:w-[30%] sm:w-[40%] sm:mt-0 mt-5">
      <span className="md:text-2xl sm:text-xl text-base text-white">Token Distribution</span>
      <div className="sm:h-[-webkit-fill-available] border border-[#2B2B2B] rounded-xl bg-[#010101] shadow-md">
        <div className="flex items-center justify-between bg-white/10 rounded-t-xl">
          <div className="flex gap-2 py-2 px-5">
            <p className="text-white 2xl:text-base text-sm font-medium">Total Issuance:</p>
            <Skeleton.Loader
              className="lg:w-[90px] w-[50px]"
              containerClassName="h-5 flex items-center"
            >
              <p className="text-white 2xl:text-base text-sm font-medium">{totalIssuance} ANLOG</p>
            </Skeleton.Loader>
          </div>
          <span className="text-white text-xl flex items-center">
            <ReactToolTip
              place="top-start"
              id="tokenchart-info"
              content="Total issuance refers to the total number of tokens minted at genesis, including both staked and vested tokens. Circulating supply represents the portion of these tokens that are transferable."
              className="max-w-[340px] md:max-w-[540px] !text-black !rounded-lg z-10 !text-xs !px-[6px] !py-[6px] md:!px-[12px] md:!py-[10px] text-pretty"
            />
            <Icon
              icon={allImgPaths.infoIcon}
              iconClasses="h-[14px] w-[14px]"
              extraClasses="bg-transparent h-auto min-w-[14px] mr-3 flex-none justify-center cursor-pointer"
              tooltipId="tokenchart-info"
            />
          </span>
        </div>
        <div className="flex items-center sm:justify-between justify-center sm:gap-4 gap-10 xl:gap-16 lg:gap-5 p-4 xl:mt-0 lg:mt-4 sm:mt-7">
          <div className="relative w-[120px] xl:w-[190px] lg:w-40 h-[120px] xl:h-[190px] lg:h-40 xl:pl-7">
            <Doughnut data={data} options={options} className="relative z-20" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 xl:translate-x-[calc(-50%+14px)] pointer-events-none z-10">
              <AnalogIcon className="w-10 h-10 shrink-0 aspect-square" />
            </div>
          </div>

          <div className="space-y-3 text-white sm:pr-[119px]">
            <ul className="sm:space-y-5 space-y-3">
              <li className="flex items-center text-sm sm:whitespace-nowrap">
                <span className="w-2 h-8 bg-lightPink rounded-[2px] mr-2"></span>
                <div className="flex flex-col text-white/70 xl:text-sm text-xs">
                  Circulating{' '}
                  <Skeleton.Loader
                    className="lg:w-[90px] w-[50px]"
                    containerClassName="h-5 flex items-center"
                  >
                    <span className="text-white xl:text-sm text-xs">
                      {circulatingSupply} ({circulatingSupplyPercentage}%)
                    </span>
                  </Skeleton.Loader>
                </div>
              </li>
              <li className="flex items-center text-sm sm:whitespace-nowrap">
                <span className="w-2 h-8 bg-[#A877B3] rounded-[2px] mr-2"></span>
                <div className="flex flex-col text-white/70 xl:text-sm text-xs">
                  Vesting{' '}
                  <Skeleton.Loader
                    className="lg:w-[90px] w-[50px]"
                    containerClassName="h-5 flex items-center"
                  >
                    <span className="text-white xl:text-sm text-xs">
                      {vesting} ({vestingPercentage}%)
                    </span>
                  </Skeleton.Loader>
                </div>
              </li>
              <li className="flex items-center text-sm sm:whitespace-nowrap">
                <span className="w-2 h-8 bg-Purple rounded-[2px] mr-2"></span>
                <div className="flex flex-col text-white/70 xl:text-sm text-xs">
                  Staking{' '}
                  <Skeleton.Loader
                    className="lg:w-[90px] w-[50px]"
                    containerClassName="h-5 flex items-center"
                  >
                    <span className="text-white xl:text-sm text-xs">
                      {staking} ({stakingPercentage}%)
                    </span>
                  </Skeleton.Loader>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
